import { ReactElement, useEffect } from 'react'
import { Box, Typography, styled } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../redux/hook'
import { updateLoader } from '../../redux/slices/loaderSlice'

/**
 * Bordered content surface below the top navbar. Mirrors the `Main` styled
 * component used by UnAuthenticatedLayout (no SideNav offset), since an orphan
 * user has no navigation rail.
 */
const Main = styled('main')(() => ({
    flexGrow: 1,
    marginLeft: '10px',
    marginRight: '10px',
    width: `calc(100% - 20px)`,
    height: 'calc(100vh - 83px)',
    border: '1px solid #959595',
    borderRadius: '8px',
    marginBottom: '20px',
    position: 'fixed',
    top: '80px',
    padding: '24px',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
}))

/**
 * Shown when an authenticated user belongs to no organization (an "orphan"
 * user — e.g. one removed from their last organization). Instead of leaving
 * the app stuck on the global loader (UBQ-5740), we explain the situation and
 * point the user at DeltaTrak support. The support contact block mirrors
 * ErrorBoundary.tsx.
 */
const NoOrganizationAccess = (): ReactElement => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const isLoading = useAppSelector((state) => state.loader.loader) || false

    useEffect(() => {
        /**
         * The app starts with the loader as true and normally clears it once an
         * org is selected. That never happens for an orphan user, so clear it
         * defensively here too.
         */
        if (isLoading) {
            dispatch(updateLoader(false))
        }
    }, [isLoading, dispatch])

    return (
        <Main>
            <Box
                id="no-organization-access"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    gap: 1.5,
                    flexGrow: 1,
                }}
            >
                <Typography variant="h5" component="h1" color="error">
                    {t('noOrganizationTitle')}
                </Typography>
                <Typography variant="body1">
                    {t('noOrganizationMessage')}
                </Typography>
                <Box>
                    <Typography variant="body2" component="span">
                        {t('noOrganizationSupportIntro')}{' '}
                        <a href="mailto:dtcloudservices@deltatrak.com">
                            dtcloudservices@deltatrak.com
                        </a>
                    </Typography>
                    <br />
                    <Typography variant="body2" component="span">
                        {t('orCall')}{' '}
                        <a href="tel:+18003900804">800-390-0804</a> (
                        {t('option')} 4) {t('orReachUsAt')}{' '}
                        <a href="tel:+19252492250">925-249-2250</a> {t('ext')}{' '}
                        5124.
                    </Typography>
                </Box>
            </Box>
        </Main>
    )
}

export default NoOrganizationAccess
